/**
 * hostedEcCompact.tsx
 *
 * Shared "is this hosted EC too short to render fully" signal for Atlas.
 *
 * Compactness is derived from the hosting Atlas item's HEIGHT (a stable value
 * that changes only on a resize commit), not from a DOM ResizeObserver on the EC
 * content. Measuring the content and setting state from an observer created a
 * feedback loop — the state flip swapped the observed element, which fired the
 * observer again — so it is done here, above the content, where the item geometry
 * is already known before the first render.
 *
 * The provider is mounted by the Atlas EC window; consumers (the Data Table body,
 * the toolbar-hidden hook) read it via {@link useHostedEcCompact}. Off Atlas there
 * is no provider, so it defaults to false and ECs render at full size.
 */

import { createContext, useContext } from "react";

/**
 * Item height (px) at/below which a hosted EC renders its compact form. Chosen so
 * a Data Table's content area — the item height minus the ~36px banner + inset —
 * falls to about 96px (four 24px rows), too short for a grid, so it shows a
 * filters-only strip instead.
 */
export const ATLAS_EC_COMPACT_MAX_ITEM_HEIGHT_PX = 132;

export function isAtlasEcItemCompact(itemHeight: number): boolean {
  return itemHeight > 0 && itemHeight <= ATLAS_EC_COMPACT_MAX_ITEM_HEIGHT_PX;
}

const HostedEcCompactContext = createContext(false);

export const HostedEcCompactProvider = HostedEcCompactContext.Provider;

export function useHostedEcCompact(): boolean {
  return useContext(HostedEcCompactContext);
}
